import React, { useCallback, useMemo, useRef, useState } from "react";

import { useNavigate, useMatch, Outlet, useLocation } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormProvider, Controller, useForm } from "react-hook-form";

import clsx from "clsx";

import map from "lodash/map";
import some from "lodash/some";

import { AnimatePresence, motion } from "framer-motion";

import useClickAway from "react-use/lib/useClickAway";

import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Autocomplete from "@mui/material/Autocomplete";

import useIdleEffect from "../../lib/hooks/useIdleEffect";

import Title from "../../components/Title";
import Modal from "../../components/Modal";
import BackButton from "../../components/BackButton";
import VirtualKeyboard, {
  LayoutType,
  VirtualKeyboardRef,
} from "../../components/VirtualKeyboard";
import Curve from "../../components/Animations/PageTransitions/Curve";

import { client as graphqlCLient } from "../../bit/board/hooks/useGraphQLClient";

// import usePageTracking from "../../bit/tracking/usePageTracking";
// import useEventTracking from "../../bit/tracking/useEventTracking";
import { useDebounceStatus } from "../../lib/hooks/useDebounceStatus";
import { useTimeoutFunction } from "../../lib/hooks/useTimeoutFunction";

import { isVirtualKeyboardEnabled } from "../../config/const";
import { MUTATION_CREATE_DELIVERY_EVENT } from "../../config/graphql/mutation";
import {
  QUERY_ACCESS_EVENT,
  QUERY_EMPLOYEES,
} from "../../config/graphql/query";
import OfflineModal from "../../components/Offline/OfflineModal";
import { useOfflineContext } from "../../context/Offline";
import { useClientPlan } from "../../lib/hooks/useQueryBoard";

import MoveLeft from "../../components/Animations/MoveLeft";

interface FieldValues {
  contact: any;
}

const DeliveryPage = () => {
  const match = useMatch({ path: "/delivery/:action" });

  const action = match?.params?.action as "package" | "food";

  const namespace = action === "food" ? "foodDelivery" : "packageDelivery";

  const { pathname } = useLocation();

  const mainRoute = new RegExp(`^\/delivery\/${action}\/?$`).test(pathname);

  const { t } = useTranslation([namespace]);

  const navigate = useNavigate();
  const { online } = useOfflineContext();

  const queryClient = useQueryClient();

  const clientPlan = useClientPlan();

  // usePageTracking({
  //   page: action,
  // });

  // const { trackEvent } = useEventTracking();

  const [calling, setCalling] = useState<boolean>(false);

  const [layout, setLayout] = useState<LayoutType>("default");
  const [focusedInput, setFocusedInput] = useState<string | undefined>(
    undefined,
  );

  const keyboard = useRef<VirtualKeyboardRef>(null);
  const keyboardBox = useRef<HTMLDivElement>(null);

  const methods = useForm<FieldValues>({
    defaultValues: {
      contact: undefined,
    },
    mode: "onSubmit",
  });

  useIdleEffect(() => {
    navigate("/", { replace: true });
  }, 60000);

  const { data: employeesData } = useQuery(
    [
      "employees",
      {
        filter: {
          preferences: {
            [namespace]: {
              enabled: {
                EQ: true,
              },
            },
          },
        },
        pagination: {
          limit: 99999,
        },
      },
    ] as const,
    ({ queryKey: [_key, variables] }) =>
      graphqlCLient.request<{ employees: IMemberEmployee[] }>(
        QUERY_EMPLOYEES,
        variables,
      ),
  );

  const employees = useMemo(() => employeesData?.employees, [employeesData]);

  const contactList = useMemo(
    () =>
      map(employees, (employee) => ({
        id: employee.id,
        label: employee.fullName,
      })),
    [employeesData],
  );

  const {
    data,
    status: requestStatus,
    mutate: onCreateDeliveryEvent,
  } = useMutation(async (input: { type: string; employee: string }) => {
    const response = await graphqlCLient.request<
      { createDeliveryEvent: IAccessEvent },
      { input: { type: string; employee: string } }
    >(MUTATION_CREATE_DELIVERY_EVENT, {
      input,
    });

    const accessScanId = response?.createDeliveryEvent?.id;

    /**
     * Prefetch access event so we don't wait when on contact page
     */
    await queryClient
      .prefetchQuery([`access/event/${accessScanId}`], () =>
        graphqlCLient.request(QUERY_ACCESS_EVENT, { id: accessScanId }),
      )
      .catch(() => null);

    return response;
  });

  const receiver = data?.createDeliveryEvent?.receiver;

  const autoEmail =
    receiver?.availablePreferences?.[`${action}Delivery`]?.autoChannel?.email;
  const autoSMS =
    receiver?.availablePreferences?.[`${action}Delivery`]?.autoChannel?.sms;
  const autoCall =
    receiver?.availablePreferences?.[`${action}Delivery`]?.autoChannel?.call;
  const autoPing =
    receiver?.availablePreferences?.[`${action}Delivery`]?.autoChannel?.ping;

  const status = useDebounceStatus(requestStatus, 1500);

  const translationKey = useMemo(() => {
    const key = `${namespace}:status.${status}`;

    if (status !== "success") {
      return key;
    }

    let messageType = "no-email-sms";

    if (autoCall || autoPing) {
      messageType = "call-ping";
    } else if (autoEmail || autoSMS) {
      messageType = "email-sms";
    }

    return `${key}.${messageType}`;
  }, [namespace, status, autoCall, autoPing, autoEmail, autoSMS]);

  const onDismiss = useCallback(() => {
    const id = data?.createDeliveryEvent?.id;

    const isFree = clientPlan && clientPlan === "free";

    /**
     * Can the user be navigated to contact page based on permissions and cations
     *
     * - Has to be non free user
     * - Employee has to have a phone number set
     * - Employee has to have at least one notification channel enabled
     */

    const canContinue =
      !isFree && !!id && status === "success" && !!receiver?.phone;

    if (canContinue) {
      if (!!autoCall || !!autoPing) {
        setCalling(true);
        return navigate(
          `/delivery/${action}/contact/${id}/${!!autoCall ? "call" : "ping"}`,
          {
            replace: true,
          },
        );
      }

      // if (!!autoSMS) {
      //   return navigate("/", { replace: true });
      // }

      if (
        some(
          receiver?.availablePreferences?.[namespace]?.channel,
          (v) => v === true,
        )
      ) {
        return navigate(`/contact/${id}`, { replace: true });
      }
    }

    return navigate("/", { replace: true });
  }, [status, data]);

  const timeout = useMemo(() => {
    if (status === "error") {
      return 10000;
    }

    if (status === "success") {
      return 3000;
    }

    return 0;
  }, [status]);

  useTimeoutFunction(onDismiss, timeout);

  const getLayoutForFieldAndValue = useCallback(
    (layout: LayoutType = "default", _field = "", value = "") => {
      if (layout === "default-alt") {
        return layout;
      }

      // Capitalize after whitespace and at start
      if (!value || value.slice(-1) === " ") {
        return "default-shift";
      }

      return "default";
    },
    [],
  );

  const onKeyboardChange = useCallback(
    (value = "") => {
      const input = document.querySelector<HTMLInputElement>(
        `[name="${focusedInput}"]`,
      );

      let selectionStart = Math.max(0, value.length - 1);

      if (input && typeof input.selectionStart === "number") {
        selectionStart = input.selectionStart;
      }

      if (!focusedInput) {
        return;
      }

      let prevValue = methods.getValues(focusedInput as any);

      setLayout((prevLayout) => {
        const nextLayout = getLayoutForFieldAndValue(
          prevLayout,
          focusedInput,
          value,
        );

        return nextLayout;
      });

      if (focusedInput === "contact") {
        if (prevValue && typeof prevValue.label === "string") {
          prevValue = prevValue.label;
        }

        methods.setValue("contact", { label: value });
      } else {
        methods.setValue(focusedInput as any, value);
      }

      if (!prevValue) {
        return;
      }

      /**
       * Update caret position of text input
       */
      if (input) {
        const position = prevValue.length - value.length;

        const next = Math.max(0, selectionStart - position);

        input.setSelectionRange(next, next);
      }
    },
    [focusedInput],
  );

  const onKeyboardKeyPress = useCallback(
    (button = "") => {
      if (button === "{alt}") {
        setLayout(
          (layout) =>
            (layout.endsWith("alt")
              ? layout.split("-").shift()
              : `${layout.split("-").shift()}-alt`) as LayoutType,
        );
      }

      if (button === "{shift}") {
        setLayout(
          (layout) =>
            (layout.endsWith("shift")
              ? layout.split("-").shift()
              : `${layout.split("-").shift()}-shift`) as LayoutType,
        );
      }
    },
    [focusedInput, layout],
  );

  useClickAway(keyboardBox, (event) => {
    if (event.target && (event.target as HTMLElement)?.tagName === "INPUT") {
      return;
    }

    setFocusedInput(undefined);
  });

  const onSubmit = useCallback((variables: FieldValues) => {
    const input = {
      type: action === "package" ? "Package" : "Food",
      employee: variables.contact.id,
    };

    // trackEvent({ type: "notify_button_click", value: action });

    return onCreateDeliveryEvent(input, {
      onSuccess() {
        // trackEvent({ type: `${action}_delivery`, value: "success" });
      },
      onError() {
        // trackEvent({ type: `${action}_delivery`, value: `$error` });
      },
    });
  }, []);

  /**
   * Synchronize text caret position with virtual keyboard
   */
  const onInputSelect = useCallback(
    (event: React.SyntheticEvent<HTMLInputElement, Event>) => {
      if (!keyboard.current) {
        return;
      }

      const { selectionStart, selectionEnd } = event.target as HTMLInputElement;

      if (
        !(
          typeof selectionStart === "number" && typeof selectionEnd === "number"
        )
      ) {
        return;
      }

      keyboard.current.setCaretPosition(selectionStart, selectionEnd);
    },
    [],
  );

  const onInputFocus = useCallback(
    (
      event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>,
    ) => {
      const {
        target: { name, value },
      } = event;

      /**
       * Skip if there is no virtual keyboard
       */
      if (!keyboard.current) {
        return;
      }

      setLayout((prevLayout) =>
        getLayoutForFieldAndValue(prevLayout, name, value),
      );

      setFocusedInput(name);
    },
    [],
  );

  /**
   * Synchronize text input with virtual keyboard
   */
  const onInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const {
        target: { name, value, selectionStart, selectionEnd },
      } = event;

      /**
       * Skip if there is no virtual keyboard
       */
      if (!keyboard.current) {
        return;
      }

      setLayout((prevLayout) =>
        getLayoutForFieldAndValue(prevLayout, name, value),
      );

      keyboard.current.setInput(value, name);
      keyboard.current.setCaretPosition(selectionStart, selectionEnd);
    },
    [],
  );

  return (
    <Curve>
      <FormProvider {...methods}>
        <div className="d-flex align-items-center flex-column">
          <BackButton to="/" />
          <Title disableAnimation={!mainRoute}>
            <Typography variant="h1">
              <Trans i18nKey={`${namespace}:title`} />
            </Typography>
            <Typography variant="h3" className="font-weight-regular mt-3">
              {t(`${namespace}:subtitle`)}
            </Typography>
          </Title>
          <form
            className="form d-flex flex-column"
            autoComplete="off"
            onSubmit={methods.handleSubmit(onSubmit)}
          >
            <MoveLeft disabled={!mainRoute} delay={0.5}>
              <motion.div className="search-input">
                <Controller
                  name="contact"
                  rules={{
                    required: {
                      value: true,
                      message: t(`${namespace}:input.error.contact.required`),
                    },
                    validate: {
                      exists: (value: any) => {
                        if (!(value && value.id)) {
                          return t(`${namespace}:input.error.contact.required`);
                        }

                        return true;
                      },
                    },
                  }}
                  render={({
                    field: { onChange, name, value },
                    fieldState: { error },
                  }) => {
                    return (
                      <Autocomplete
                        clearOnBlur
                        blurOnSelect
                        disableClearable
                        value={!!(value && value.id) ? value : null}
                        options={contactList}
                        getOptionLabel={(option) => option.label || ""}
                        isOptionEqualToValue={(option, value) =>
                          value?.id === option?.id
                        }
                        renderOption={(props, option) => (
                          <li {...props} key={option.id}>
                            {option.label}
                          </li>
                        )}
                        onChange={(_event, newValue, reason) => {
                          if (reason === "selectOption") {
                            onChange(newValue);
                            // trackEvent({ type: "contact_select", value: action });
                          }
                        }}
                        onBlur={() => {
                          const currentValue = methods.getValues(name);

                          if (!!(currentValue && currentValue.id)) {
                            return;
                          }

                          if (keyboard.current) {
                            keyboard.current.setInput("", name);
                          }

                          onChange(undefined);
                        }}
                        inputValue={!!(value && value.label) ? value.label : ""}
                        renderInput={(params) => {
                          return (
                            <TextField
                              {...params}
                              type="text"
                              name="contact"
                              variant="outlined"
                              error={!!error}
                              helperText={error?.message ?? " "}
                              label={t(`${namespace}:input.label.contact`)}
                              className={error && "error__field"}
                              onChange={(event) => {
                                const {
                                  target: { value },
                                } = event;
                                onInputChange(event);

                                onChange({
                                  label: value,
                                });
                              }}
                              onFocus={onInputFocus}
                              onSelect={onInputSelect}
                            />
                          );
                        }}
                      />
                    );
                  }}
                />
              </motion.div>
            </MoveLeft>
            <MoveLeft disabled={!mainRoute} delay={0.6}>
              <motion.div className="w-100">
                <Button
                  variant="contained"
                  className="mx-1 w-100"
                  disabled={!online}
                  onClick={methods.handleSubmit(onSubmit)}
                >
                  {t(`${namespace}:button.submit`)}
                </Button>
              </motion.div>
            </MoveLeft>
          </form>
          <AnimatePresence mode="wait">
            {status !== "idle" && !calling && (
              <Modal
                status={status}
                // @ts-ignore
                title={<Trans i18nKey={`${translationKey}.title`} />}
                // @ts-ignore
                message={<Trans i18nKey={`${translationKey}.message`} />}
                onDismiss={onDismiss}
              />
            )}
          </AnimatePresence>
          {isVirtualKeyboardEnabled && (
            <div
              ref={keyboardBox}
              className={clsx("keyboard ", { keyboard__out: !focusedInput })}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <VirtualKeyboard
                ref={keyboard}
                layoutName={layout}
                inputName={focusedInput}
                onChange={onKeyboardChange}
                onKeyPress={onKeyboardKeyPress}
              />
            </div>
          )}
        </div>
      </FormProvider>
      <Outlet />
      <OfflineModal />
    </Curve>
  );
};

export default DeliveryPage;
